import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  ArrowLeft,
  DownloadSimple,
  MagnifyingGlass,
  CheckCircle,
  Warning,
  XCircle,
  Clock,
  Spinner,
  FileText,
  FileCsv,
  FileXls,
  Trash,
  X,
  PaperPlaneTilt,
  PhoneCall,
  Plus,
  CaretDown
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CollapsibleList = ({ items, label, color = "text-slate-500", previewCount = 6 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!items || items.length === 0) return null;
  const visible = expanded ? items : items.slice(0, previewCount);
  const extra = items.length - previewCount;
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-1">
      {label && <span className="text-slate-400 mr-1">{label}:</span>}
      <span className={color}>{visible.join(", ")}</span>
      {extra > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-[#006994] hover:text-[#005A80] text-xs font-medium ml-1"
          data-testid={`toggle-${label?.toLowerCase() || "list"}`}
        >
          {expanded ? "Show less" : `+${extra} more`}
        </button>
      )}
    </span>
  );
};

const JobMeta = ({ category, location }) => {
  const cats = (category || "").split(",").map(s => s.trim()).filter(Boolean);
  const locs = (location || "").split(";").map(s => s.trim()).filter(Boolean);
  return (
    <div className="text-slate-500 mt-1.5 text-sm leading-relaxed flex flex-col gap-1">
      <CollapsibleList items={cats} label="Categories" color="text-slate-500" previewCount={6} />
      <CollapsibleList items={locs} label="Locations" color="text-slate-500" previewCount={6} />
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { bg: "bg-fuchsia-100", text: "text-fuchsia-700", icon: Clock, label: "Pending" },
    processing: { bg: "bg-blue-100", text: "text-blue-700", icon: Spinner, label: "Processing" },
    completed: { bg: "bg-emerald-100", text: "text-emerald-700", icon: CheckCircle, label: "Completed" },
    failed: { bg: "bg-red-100", text: "text-red-700", icon: XCircle, label: "Failed" }
  };
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
      <Icon size={16} className={status === "processing" ? "animate-spin" : ""} />
      {config.label}
    </span>
  );
};

const VerificationBadge = ({ status }) => {
  const config = {
    valid: { class: "badge-valid", icon: CheckCircle },
    risky: { class: "badge-risky", icon: Warning },
    invalid: { class: "badge-invalid", icon: XCircle },
    pending: { class: "badge-pending", icon: Clock },
    error: { class: "badge-invalid", icon: XCircle }
  };
  const { class: className, icon: Icon } = config[status] || config.pending;
  return (
    <span className={className}>
      <Icon size={12} className="mr-1" />
      {status}
    </span>
  );
};

const StatBox = ({ label, value, color }) => (
  <div className={`p-4 rounded-lg border ${color}`}>
    <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
    <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
  </div>
);

const JobDetailsPage = () => {
  const { jobId } = useParams();
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [rawLeads, setRawLeads] = useState([]);
  const [cleanedLeads, setCleanedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [exporting, setExporting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Instantly push state
  const [showInstantlyModal, setShowInstantlyModal] = useState(false);
  const [instantlyMode, setInstantlyMode] = useState("existing"); // existing | new
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignSearch, setCampaignSearch] = useState("");
  const [newCampaignName, setNewCampaignName] = useState("");
  const [includeRisky, setIncludeRisky] = useState(false);
  const [pushToInstantly, setPushToInstantly] = useState(true);
  const [pushToGHL, setPushToGHL] = useState(true);
  // Email sequence mode — "hyperlocal" (default: warm, store-owner style)
  // vs "agency_reach_out" (B2B Retail Media pitch aimed at ad agencies).
  // Determines which system prompt / template pack the AI sequence generator
  // uses when writing the follow-up emails for this campaign.
  const [emailMode, setEmailMode] = useState("hyperlocal");
  // Whether to run the AI sequence generator on this push.
  //  - NEW campaign  → always ON (no existing sequences to preserve)
  //  - EXISTING campaign → we ask the user Yes/No at push time before deciding.
  // This state is kept only so the API payload can be built consistently.
  const [generateSequences, setGenerateSequences] = useState(true);
  useEffect(() => {
    // Default the flag by mode. For existing campaigns the user is prompted
    // via window.confirm at click time, and the actual value is set right
    // before the API call — so this default is just a safety net.
    setGenerateSequences(instantlyMode === "new");
  }, [instantlyMode]);
  const [pushing, setPushing] = useState(false);
  const [pushHistory, setPushHistory] = useState([]);
  const [pushHistoryLoading, setPushHistoryLoading] = useState(false);
  const [ghlMappings, setGhlMappings] = useState({});
  const [campaignUsage, setCampaignUsage] = useState(null); // { used_by_other_jobs, other_jobs, same_job_used, ... }
  const [campaignUsageLoading, setCampaignUsageLoading] = useState(false);

  // Voice Drop (GHL workflow) push state
  const [showVoiceDropModal, setShowVoiceDropModal] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [voiceFilter, setVoiceFilter] = useState("with_phone"); // "with_phone" | "valid_only" | "all_cleaned"
  const [voicePushing, setVoicePushing] = useState(false);
  const [workflowHistory, setWorkflowHistory] = useState([]);
  const [confirmCrossJob, setConfirmCrossJob] = useState(false);
  const [confirmRepush, setConfirmRepush] = useState(false);

  // Owner + caller-ID selection for Voice Drop. Both are optional and remembered
  // per workflow so the user picks once and every future push into that workflow
  // routes to the same GHL user + number automatically.
  const [ghlUsers, setGhlUsers] = useState([]);
  const [ghlNumbers, setGhlNumbers] = useState([]);
  const [ghlLookupsLoaded, setGhlLookupsLoaded] = useState(false);
  const [ghlUsersDiag, setGhlUsersDiag] = useState(null);
  const [ghlNumbersDiag, setGhlNumbersDiag] = useState(null);
  const [manualOwnerMode, setManualOwnerMode] = useState(false);
  const [voiceDropOwnerId, setVoiceDropOwnerId] = useState("");
  const [voiceDropPhoneNumber, setVoiceDropPhoneNumber] = useState("");
  const [voiceConfigSaving, setVoiceConfigSaving] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  useEffect(() => {
    fetchJobDetails();
    if (isAdmin) {
      fetchPushHistory();
      fetchGhlMappings();
    }
    const interval = setInterval(() => {
      if (job?.status === "processing") fetchJobDetails();
    }, 5000);
    return () => clearInterval(interval);
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const [jobRes, rawRes, cleanedRes] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/leads/raw/${jobId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/leads/cleaned/${jobId}`, { headers: getAuthHeaders() })
      ]);
      setJob(jobRes.data);
      setRawLeads(rawRes.data);
      setCleanedLeads(cleanedRes.data);
    } catch (error) {
      toast.error("Failed to load job details");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchPushHistory = async () => {
    setPushHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/integrations/instantly/push-history/${jobId}`, { headers: getAuthHeaders() });
      setPushHistory(res.data);
    } catch {
      // Not critical
    } finally {
      setPushHistoryLoading(false);
    }
  };

  // Fetch campaign usage when an existing campaign is selected, to warn about cross-job pushes
  useEffect(() => {
    if (instantlyMode !== "existing" || !selectedCampaign?.id || !jobId) {
      setCampaignUsage(null);
      setConfirmCrossJob(false);
      setConfirmRepush(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setCampaignUsageLoading(true);
      try {
        const res = await axios.get(
          `${API}/integrations/instantly/campaign-usage/${selectedCampaign.id}?job_id=${jobId}`,
          { headers: getAuthHeaders() }
        );
        if (!cancelled) {
          setCampaignUsage(res.data);
          setConfirmCrossJob(false);
          setConfirmRepush(false);
        }
      } catch {
        if (!cancelled) setCampaignUsage(null);
      } finally {
        if (!cancelled) setCampaignUsageLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaign, instantlyMode, jobId]);

  const fetchGhlMappings = async () => {
    try {
      const res = await axios.get(`${API}/integrations/ghl/lead-mappings/${jobId}`, { headers: getAuthHeaders() });
      const map = {};
      for (const m of res.data) {
        map[m.lead_email] = m;
      }
      setGhlMappings(map);
    } catch {}
  };

  const fetchCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      const res = await axios.get(`${API}/integrations/instantly/campaigns`, { headers: getAuthHeaders() });
      setCampaigns(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load campaigns");
    } finally {
      setCampaignsLoading(false);
    }
  };

  const openInstantlyModal = () => {
    setShowInstantlyModal(true);
    setInstantlyMode("existing");
    setSelectedCampaign(null);
    setCampaignSearch("");
    setNewCampaignName("");
    setIncludeRisky(false);
    fetchCampaigns();
  };

  // ============== Voice Drop / GHL Workflow push ==============
  const fetchWorkflows = async () => {
    setWorkflowsLoading(true);
    try {
      const res = await axios.get(`${API}/ghl/workflows`, { headers: getAuthHeaders() });
      setWorkflows(res.data.workflows || []);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to load GHL workflows";
      toast.error(msg);
      setWorkflows([]);
    } finally {
      setWorkflowsLoading(false);
    }
  };

  const fetchWorkflowHistory = async () => {
    try {
      const res = await axios.get(`${API}/jobs/${jobId}/workflow-pushes`, { headers: getAuthHeaders() });
      setWorkflowHistory(res.data || []);
    } catch (err) {
      // Silent — push history is non-essential; the modal still works without it.
      console.warn("Could not load workflow push history:", err?.message);
    }
  };

  const fetchGhlLookups = async () => {
    // Load users + numbers in parallel — both are optional so a failure just
    // leaves the dropdown empty (falls back to a free-text input for numbers).
    try {
      const [uRes, nRes] = await Promise.all([
        axios.get(`${API}/integrations/voice-drop/ghl-users`, { headers: getAuthHeaders() }).catch((e) => ({ data: { users: [], diagnostic: { hint: e.response?.data?.detail || "Failed to load GHL users." } } })),
        axios.get(`${API}/integrations/voice-drop/ghl-numbers`, { headers: getAuthHeaders() }).catch((e) => ({ data: { numbers: [], diagnostic: { hint: e.response?.data?.detail || "" } } })),
      ]);
      setGhlUsers(uRes.data?.users || []);
      setGhlNumbers(nRes.data?.numbers || []);
      setGhlUsersDiag(uRes.data?.diagnostic || null);
      setGhlNumbersDiag(nRes.data?.diagnostic || null);
      // Auto-flip to manual owner mode when the dropdown is empty AND we have
      // an actionable diagnostic — otherwise the admin sees a dead dropdown.
      if ((uRes.data?.users || []).length === 0 && (uRes.data?.diagnostic?.hint || "")) {
        setManualOwnerMode(true);
      }
    } finally {
      setGhlLookupsLoaded(true);
    }
  };

  // Whenever the user picks a workflow, load the saved owner + phone-number
  // defaults for that workflow so the dropdowns pre-fill and the push carries
  // through the right assignedTo / caller-id metadata.
  useEffect(() => {
    if (!showVoiceDropModal) return;
    if (!selectedWorkflow?.id) {
      setVoiceDropOwnerId("");
      setVoiceDropPhoneNumber("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/integrations/voice-drop/workflow-config/${selectedWorkflow.id}`,
          { headers: getAuthHeaders() },
        );
        if (cancelled) return;
        setVoiceDropOwnerId(res.data?.owner_id || "");
        setVoiceDropPhoneNumber(res.data?.phone_number || "");
      } catch (err) {
        if (cancelled) return;
        setVoiceDropOwnerId("");
        setVoiceDropPhoneNumber("");
      }
    })();
    return () => { cancelled = true; };
  }, [selectedWorkflow, showVoiceDropModal]);

  const openVoiceDropModal = () => {
    setShowVoiceDropModal(true);
    setSelectedWorkflow(null);
    setVoiceFilter("with_phone");
    fetchWorkflows();
    fetchWorkflowHistory();
    if (!ghlLookupsLoaded) fetchGhlLookups();
  };

  const handlePushToVoiceDrop = async () => {
    if (!selectedWorkflow) {
      toast.error("Pick a workflow first");
      return;
    }
    if (!voiceDropOwnerId) {
      toast.error("Please assign an owner before pushing.");
      return;
    }
    if (!voiceDropPhoneNumber) {
      toast.error("Please select a caller-ID number before pushing.");
      return;
    }
    setVoicePushing(true);
    try {
      // Persist per-workflow owner + phone defaults FIRST so the backend
      // picks them up when it upserts contacts. Idempotent PUT — empty
      // values simply clear whatever was saved before.
      const ownerName = ghlUsers.find(u => u.id === voiceDropOwnerId)?.name || "";
      await axios.put(
        `${API}/integrations/voice-drop/workflow-config/${selectedWorkflow.id}`,
        {
          owner_id: voiceDropOwnerId || "",
          owner_name: ownerName,
          phone_number: voiceDropPhoneNumber || "",
        },
        { headers: getAuthHeaders() },
      ).catch(() => {
        // Non-fatal — the push still works, just without the saved defaults.
      });

      const res = await axios.post(
        `${API}/jobs/${jobId}/push-to-workflow`,
        { workflow_id: selectedWorkflow.id, filter: voiceFilter },
        { headers: getAuthHeaders() },
      );
      const { queued, skipped_already_enrolled, status } = res.data;
      const isSuperAdmin = user?.role === "super_admin";
      if (status === "pending_approval") {
        // Regular admins see a "started" toast — the approval gate is a
        // super-admin-only concern. Super admins see the true state so they
        // know to head to Analytics → Pending Approvals to finish the flow.
        if (isSuperAdmin) {
          toast.success(
            `Voice Drop queued for approval — ${queued} contact(s) into "${selectedWorkflow.name}". Approve in Analytics → Pending Approvals.`,
            { duration: 8000 },
          );
        } else {
          toast.success(
            `Voice Drop started — enrolling ${queued} contact(s) into "${selectedWorkflow.name}". Sync in progress.`,
            { duration: 7000 },
          );
        }
      } else {
        // Legacy path (kept for graceful fallback).
        toast.success(
          `Enrolled ${queued || 0} contact(s) in "${selectedWorkflow.name}".` +
            (skipped_already_enrolled ? ` ${skipped_already_enrolled} already enrolled.` : ""),
        );
      }
      setShowVoiceDropModal(false);
      fetchWorkflowHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to push to workflow");
    } finally {
      setVoicePushing(false);
    }
  };

  // Save owner + phone defaults for a workflow without triggering a push.
  const handleSaveVoiceDropConfig = async () => {
    if (!selectedWorkflow) return;
    setVoiceConfigSaving(true);
    try {
      const ownerName = ghlUsers.find(u => u.id === voiceDropOwnerId)?.name || "";
      await axios.put(
        `${API}/integrations/voice-drop/workflow-config/${selectedWorkflow.id}`,
        {
          owner_id: voiceDropOwnerId || "",
          owner_name: ownerName,
          phone_number: voiceDropPhoneNumber || "",
        },
        { headers: getAuthHeaders() },
      );
      toast.success(`Defaults saved for "${selectedWorkflow.name}".`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save workflow defaults");
    } finally {
      setVoiceConfigSaving(false);
    }
  };

  const handlePushToInstantly = async () => {
    let campaignId = selectedCampaign?.id;
    let campaignName = selectedCampaign?.name;

    // Sequence-generation policy:
    //  - NEW campaign     → always generate (there's nothing to overwrite)
    //  - EXISTING campaign → prompt Yes/No; No pushes leads without touching
    //    the campaign's sequences, Yes overwrites them with fresh AI copy.
    let willGenerateSequences = true;
    if (instantlyMode === "existing") {
      if (!selectedCampaign) {
        toast.error("Please select a campaign");
        return;
      }
      willGenerateSequences = window.confirm(
        `Generate new AI email sequences for "${selectedCampaign.name}"?\n\n` +
        "• Click OK to have AI write fresh sequences — this will OVERWRITE the campaign's current email sequences.\n" +
        "• Click Cancel to push the leads only and leave the existing sequences untouched.",
      );
    }
    setGenerateSequences(willGenerateSequences);

    // Create new campaign if needed
    if (instantlyMode === "new") {
      if (!newCampaignName.trim()) {
        toast.error("Please enter a campaign name");
        return;
      }
      setPushing(true);
      try {
        const res = await axios.post(`${API}/integrations/instantly/campaigns`, { name: newCampaignName.trim() }, { headers: getAuthHeaders() });
        campaignId = res.data.id;
        campaignName = res.data.name;
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to create campaign");
        setPushing(false);
        return;
      }
    }

    setPushing(true);
    try {
      const res = await axios.post(`${API}/integrations/instantly/push`, {
        job_id: jobId,
        campaign_id: campaignId,
        campaign_name: campaignName,
        include_risky: includeRisky,
        push_to_instantly: pushToInstantly,
        push_to_ghl: pushToGHL,
        email_mode: emailMode,
        generate_sequences: willGenerateSequences,
        confirm_cross_job: confirmCrossJob,
        confirm_repush: confirmRepush
      }, { headers: getAuthHeaders(), timeout: 30000 });

      const d = res.data;
      toast.success(`Push started for ${d.leads_to_push} leads to "${d.campaign_name}". Check Push History for results.`);
      setShowInstantlyModal(false);
      fetchPushHistory();
      // Auto-poll push history for updates
      const pollInterval = setInterval(() => {
        fetchPushHistory();
        fetchGhlMappings();
      }, 10000);
      setTimeout(() => clearInterval(pollInterval), 300000); // Stop polling after 5 min
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.info("Push is processing. Check Push History for results.");
        setShowInstantlyModal(false);
        fetchPushHistory();
      } else {
        toast.error(detail || "Failed to push leads");
      }
    } finally {
      setPushing(false);
    }
  };


  const handleGhlResync = async (pushId) => {
    try {
      toast.info("GHL re-sync started...");
      await axios.post(`${API}/integrations/ghl/resync`, { push_id: pushId }, { headers: getAuthHeaders() });
      // Poll for updates
      const pollInterval = setInterval(() => fetchPushHistory(), 8000);
      setTimeout(() => clearInterval(pollInterval), 300000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start GHL re-sync");
    }
  };

  const handleExport = async (format, includeAll = false, validOnly = false, excludeInvalid = false) => {
    setExporting(true);
    try {
      const response = await axios.post(
        `${API}/exports/${jobId}`,
        { format, include_all: includeAll, valid_only: validOnly, exclude_invalid: excludeInvalid },
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      const disposition = response.headers['content-disposition'];
      let filename = `leads_export.${format === 'xlsx' ? 'xlsx' : 'csv'}`;
      if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 200);
      const exportType = validOnly ? 'valid only' : (excludeInvalid ? 'valid & risky' : (includeAll ? 'all' : 'cleaned'));
      toast.success(`Exported ${exportType} leads as ${format.toUpperCase()}`);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== "DELETE") return;
    try {
      await axios.delete(`${API}/jobs/${jobId}`, { headers: getAuthHeaders() });
      toast.success("Job deleted");
      navigate("/dashboard");
    } catch {
      toast.error("Failed to delete job");
    }
  };

  const filteredLeads = cleanedLeads.filter(lead => {
    const matchesSearch = !searchQuery ||
      lead.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.phone?.includes(searchQuery);
    const matchesStatus = !statusFilter || lead.verification_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredCampaigns = campaigns.filter(c =>
    c.name?.toLowerCase().includes(campaignSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#006994] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="space-y-6" data-testid="job-details-page">
      {/* Voice Drop / GHL Workflow Modal */}
      {showVoiceDropModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4" onClick={() => !voicePushing && setShowVoiceDropModal(false)}>
          <div className="bg-white border border-slate-200 rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <PhoneCall size={20} className="text-[#006994]" />
                <h3 className="text-lg font-bold text-slate-900">Push to Voice Drop / GHL Workflow</h3>
              </div>
              <button onClick={() => !voicePushing && setShowVoiceDropModal(false)} className="text-slate-500 hover:text-slate-900" data-testid="voice-drop-close-btn">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto">
              <div>
                <div className="text-sm text-slate-700 mb-1.5 block font-medium">Workflow</div>
                {workflowsLoading ? (
                  <div className="flex items-center gap-2 text-slate-500 text-sm py-3">
                    <Spinner size={16} className="animate-spin" /> Loading workflows from GHL…
                  </div>
                ) : workflows.length === 0 ? (
                  <div className="text-sm text-slate-500 bg-slate-100/40 border border-slate-200 rounded p-3">
                    No workflows found in your GHL sub-account. Build one first in GHL → Automation → Workflows → New Workflow → &ldquo;Voice Mail Drop&rdquo; template.
                  </div>
                ) : (
                  <select
                    value={selectedWorkflow?.id || ""}
                    onChange={(e) => setSelectedWorkflow(workflows.find(w => w.id === e.target.value) || null)}
                    className="w-full bg-slate-100 border border-slate-200 rounded px-3 py-2 text-slate-900 text-sm focus:outline-none focus:border-[#006994]"
                    data-testid="voice-drop-workflow-select"
                  >
                    <option value="">-- pick a workflow --</option>
                    {workflows.map(wf => (
                      <option key={wf.id} value={wf.id}>
                        {wf.name} {wf.status && wf.status !== "published" ? `(${wf.status})` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <div className="text-sm text-slate-700 mb-1.5 block font-medium">Which leads</div>
                <div className="space-y-2 text-sm">
                  <label className="flex items-center gap-2 text-slate-700 cursor-pointer">
                    <input type="radio" name="voice-filter" value="with_phone" checked={voiceFilter === "with_phone"} onChange={(e) => setVoiceFilter(e.target.value)} data-testid="voice-filter-with-phone" />
                    <span><strong>With phone</strong> — only leads with a valid US phone ({job.phone_count || 0})</span>
                  </label>
                  <label className="flex items-center gap-2 text-slate-700 cursor-pointer">
                    <input type="radio" name="voice-filter" value="valid_only" checked={voiceFilter === "valid_only"} onChange={(e) => setVoiceFilter(e.target.value)} data-testid="voice-filter-valid" />
                    <span><strong>Valid emails only</strong> — leads with verified emails ({job.verified_count || 0})</span>
                  </label>
                  <label className="flex items-center gap-2 text-slate-700 cursor-pointer">
                    <input type="radio" name="voice-filter" value="all_cleaned" checked={voiceFilter === "all_cleaned"} onChange={(e) => setVoiceFilter(e.target.value)} data-testid="voice-filter-all" />
                    <span><strong>All cleaned</strong> — every cleaned lead ({job.cleaned_leads_count || 0})</span>
                  </label>
                </div>
              </div>

              {selectedWorkflow && (
                <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Workflow defaults
                    </div>
                    <span className="text-[10px] text-slate-500">
                      Remembered per workflow
                    </span>
                  </div>

                  <div>
                    <label className="text-xs text-slate-600 mb-1 block flex items-center justify-between">
                      <span>Assign contacts to owner <span className="text-red-500">*</span></span>
                      {ghlUsers.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setManualOwnerMode(m => !m)}
                          className="text-[10px] text-[#006994] hover:underline"
                          data-testid="voice-drop-owner-toggle-manual"
                        >
                          {manualOwnerMode ? "Use dropdown" : "Enter user ID manually"}
                        </button>
                      )}
                    </label>
                    {manualOwnerMode ? (
                      <input
                        type="text"
                        value={voiceDropOwnerId}
                        onChange={(e) => setVoiceDropOwnerId(e.target.value.trim())}
                        placeholder="Paste GHL user ID (e.g. 8fXaB7z…)"
                        className={`w-full bg-white border rounded px-3 py-2 text-slate-900 text-sm focus:outline-none focus:border-[#006994] font-mono ${voiceDropOwnerId ? "border-slate-200" : "border-red-300"}`}
                        data-testid="voice-drop-owner-input"
                      />
                    ) : (
                      <select
                        value={voiceDropOwnerId}
                        onChange={(e) => setVoiceDropOwnerId(e.target.value)}
                        className={`w-full bg-white border rounded px-3 py-2 text-slate-900 text-sm focus:outline-none focus:border-[#006994] ${voiceDropOwnerId ? "border-slate-200" : "border-red-300"}`}
                        data-testid="voice-drop-owner-select"
                        required
                      >
                        <option value="">— Select an owner —</option>
                        {ghlUsers.map(u => (
                          <option key={u.id} value={u.id}>
                            {u.name}{u.email ? ` (${u.email})` : ""}
                          </option>
                        ))}
                      </select>
                    )}
                    {ghlUsers.length === 0 && ghlLookupsLoaded && !manualOwnerMode && (
                      <div className="mt-1 p-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800 leading-relaxed" data-testid="voice-drop-owner-diagnostic">
                        <div className="font-semibold mb-0.5">GHL user list unavailable</div>
                        <div>{ghlUsersDiag?.hint || "No users returned. Regenerate the Private Integration Token in GHL with the `View Users` scope, or paste a user ID manually below."}</div>
                        <button
                          type="button"
                          onClick={() => setManualOwnerMode(true)}
                          className="mt-1 text-[11px] text-[#006994] hover:underline font-semibold"
                          data-testid="voice-drop-owner-switch-manual"
                        >
                          → Enter GHL user ID manually
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-xs text-slate-600 mb-1 block">
                      Voice Drop caller-ID / from-number <span className="text-red-500">*</span>
                    </label>
                    {ghlNumbers.length > 0 ? (
                      <select
                        value={voiceDropPhoneNumber}
                        onChange={(e) => setVoiceDropPhoneNumber(e.target.value)}
                        className={`w-full bg-white border rounded px-3 py-2 text-slate-900 text-sm focus:outline-none focus:border-[#006994] ${voiceDropPhoneNumber ? "border-slate-200" : "border-red-300"}`}
                        data-testid="voice-drop-number-select"
                        required
                      >
                        <option value="">— Select a caller-ID number —</option>
                        {ghlNumbers.map(n => (
                          <option key={n.id} value={n.phone_number}>
                            {n.phone_number}{n.label ? ` — ${n.label}` : ""}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="tel"
                        value={voiceDropPhoneNumber}
                        onChange={(e) => setVoiceDropPhoneNumber(e.target.value)}
                        placeholder="+15551234567"
                        className={`w-full bg-white border rounded px-3 py-2 text-slate-900 text-sm focus:outline-none focus:border-[#006994] ${voiceDropPhoneNumber ? "border-slate-200" : "border-red-300"}`}
                        data-testid="voice-drop-number-input"
                        required
                      />
                    )}
                    <p className="text-[11px] text-slate-500 mt-1">
                      Pulled live from your GHL sub-account. Your selection is
                      saved per workflow so every push through this workflow
                      dials from the same caller-ID.
                    </p>
                  </div>

                  <button
                    onClick={handleSaveVoiceDropConfig}
                    disabled={voiceConfigSaving}
                    className="text-xs text-[#006994] hover:text-[#005A80] font-medium disabled:opacity-50"
                    data-testid="voice-drop-save-defaults-btn"
                  >
                    {voiceConfigSaving ? "Saving…" : "Save defaults without pushing"}
                  </button>
                </div>
              )}

              {selectedWorkflow && (
                <div className="bg-[#006994]/5 border border-[#006994]/20 rounded p-3 text-xs text-slate-700">
                  Each lead will be upserted into GHL as a contact (using <strong>phone_e164</strong> as the key for phone-only leads, email otherwise), then enrolled into <strong>{selectedWorkflow.name}</strong>. Leads already enrolled in this workflow are skipped automatically.
                </div>
              )}

              {workflowHistory.length > 0 && (
                <details className="text-xs text-slate-500">
                  <summary className="cursor-pointer hover:text-slate-900">Previous voice-drop pushes ({workflowHistory.length})</summary>
                  <div className="mt-2 space-y-1 pl-3 border-l border-slate-200">
                    {workflowHistory.slice(0, 5).map(p => (
                      <div key={p.id}>
                        {new Date(p.started_at).toLocaleString()} — pushed {p.pushed}/{p.to_push || p.total_eligible} ({p.failed} failed) by {p.pushed_by}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200 flex-shrink-0">
              <Button variant="outline" onClick={() => setShowVoiceDropModal(false)} disabled={voicePushing} data-testid="voice-drop-cancel-btn">Cancel</Button>
              <Button
                onClick={handlePushToVoiceDrop}
                disabled={voicePushing || !selectedWorkflow || !voiceDropOwnerId || !voiceDropPhoneNumber}
                className="bg-[#006994] hover:bg-[#005A80] text-white font-bold"
                data-testid="voice-drop-confirm-btn"
              >
                {voicePushing ? (
                  <><Spinner size={16} className="mr-2 animate-spin" /> Enrolling…</>
                ) : (
                  <><PhoneCall size={16} className="mr-2" /> Enroll Contacts</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Instantly Push Modal */}
      {showInstantlyModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4" onClick={() => !pushing && setShowInstantlyModal(false)}>
          <div className="bg-white border border-slate-200 rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <PaperPlaneTilt size={20} className="text-emerald-600" />
                <h3 className="text-lg font-bold text-slate-900">Push to Instantly</h3>
              </div>
              <button onClick={() => !pushing && setShowInstantlyModal(false)} className="text-slate-500 hover:text-slate-900">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Mode toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setInstantlyMode("existing")}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${instantlyMode === "existing" ? "bg-emerald-600/15 text-emerald-700 border border-emerald-600/50" : "bg-slate-100 text-slate-500 border border-slate-200 hover:border-slate-400"}`}
                  data-testid="mode-existing-btn"
                >
                  Select Existing Campaign
                </button>
                <button
                  onClick={() => setInstantlyMode("new")}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${instantlyMode === "new" ? "bg-emerald-600/15 text-emerald-700 border border-emerald-600/50" : "bg-slate-100 text-slate-500 border border-slate-200 hover:border-slate-400"}`}
                  data-testid="mode-new-btn"
                >
                  <Plus size={14} className="inline mr-1" /> Create New
                </button>
              </div>

              {/* Existing campaign selector */}
              {instantlyMode === "existing" && (
                <div className="space-y-2">
                  <Input
                    value={campaignSearch}
                    onChange={e => setCampaignSearch(e.target.value)}
                    placeholder="Search campaigns..."
                    className="bg-white/50 border-slate-200 text-slate-900"
                    data-testid="campaign-search-input"
                  />
                  <div className="max-h-48 overflow-y-auto rounded-md border border-slate-200">
                    {campaignsLoading ? (
                      <div className="p-4 text-center text-slate-500 text-sm">Loading campaigns...</div>
                    ) : filteredCampaigns.length > 0 ? (
                      filteredCampaigns.map(c => (
                        <div
                          key={c.id}
                          onClick={() => setSelectedCampaign(c)}
                          className={`flex items-center justify-between px-3 py-2.5 cursor-pointer border-b border-slate-200 transition-colors ${selectedCampaign?.id === c.id ? "bg-emerald-600/15 text-emerald-700" : "text-slate-700 hover:bg-slate-100"}`}
                          data-testid={`campaign-item-${c.id}`}
                        >
                          <span className="text-sm truncate">{c.name}</span>
                          {selectedCampaign?.id === c.id && <CheckCircle size={16} />}
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-slate-500 text-sm">No campaigns found</div>
                    )}
                  </div>

                  {/* Cross-job campaign reuse warning */}
                  {selectedCampaign && campaignUsage?.used_by_other_jobs && !campaignUsageLoading && (
                    <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="cross-job-warning">
                      <div className="flex items-start gap-2">
                        <Warning size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-amber-300">Campaign already used by another job</p>
                          <p className="text-xs text-amber-200/80 mt-1 leading-relaxed">
                            Pushing to <span className="font-semibold">"{selectedCampaign.name}"</span> will mix leads with:
                          </p>
                          <ul className="text-xs text-amber-100 mt-1.5 space-y-0.5 ml-4 list-disc">
                            {campaignUsage.other_jobs.slice(0, 3).map(j => (
                              <li key={j.job_id}>
                                <span className="font-medium">{j.job_name}</span>
                                <span className="text-amber-200/70"> — {j.lead_count.toLocaleString()} leads pushed</span>
                              </li>
                            ))}
                            {campaignUsage.other_jobs.length > 3 && (
                              <li className="text-amber-200/70">+{campaignUsage.other_jobs.length - 3} more…</li>
                            )}
                          </ul>
                        </div>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer pt-1 border-t border-amber-500/20 pl-6">
                        <input
                          type="checkbox"
                          checked={confirmCrossJob}
                          onChange={(e) => setConfirmCrossJob(e.target.checked)}
                          className="accent-amber-400"
                          data-testid="confirm-cross-job-checkbox"
                        />
                        <span className="text-xs text-amber-200">I understand, push anyway</span>
                      </label>
                    </div>
                  )}

                  {/* Same-job re-push warning (e.g. campaign bounced and was wiped in Instantly) */}
                  {selectedCampaign && campaignUsage?.same_job_used && !campaignUsageLoading && (
                    <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="same-job-repush-warning">
                      <div className="flex items-start gap-2">
                        <Warning size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-amber-300">This campaign already has push history from this job</p>
                          <p className="text-xs text-amber-200/80 mt-1 leading-relaxed">
                            <span className="font-semibold">{campaignUsage.same_job_lead_count.toLocaleString()}</span> lead{campaignUsage.same_job_lead_count === 1 ? "" : "s"} were already pushed to <span className="font-semibold">"{selectedCampaign.name}"</span> across {campaignUsage.same_job_push_count} push{campaignUsage.same_job_push_count === 1 ? "" : "es"}. By default, those leads will be skipped to avoid duplicates. Enable the override only if you wiped this campaign in Instantly (e.g. it bounced) and need to re-push everything.
                          </p>
                        </div>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer pt-1 border-t border-amber-500/20 pl-6">
                        <input
                          type="checkbox"
                          checked={confirmRepush}
                          onChange={(e) => setConfirmRepush(e.target.checked)}
                          className="accent-amber-400"
                          data-testid="confirm-repush-checkbox"
                        />
                        <span className="text-xs text-amber-200">Override dedup and re-push all eligible leads</span>
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* New campaign form */}
              {instantlyMode === "new" && (
                <div className="space-y-2">
                  <label className="text-sm text-slate-500">Campaign Name</label>
                  <Input
                    value={newCampaignName}
                    onChange={e => setNewCampaignName(e.target.value)}
                    placeholder="e.g., Dentists Q1 2026"
                    className="bg-white/50 border-slate-200 text-slate-900"
                    data-testid="new-campaign-name-input"
                  />
                </div>
              )}

              {/* Email sequence mode selector */}
              <div className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20" data-testid="email-mode-selector">
                <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-2">Email Sequence Template</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label
                    className={`flex items-start gap-2 p-2.5 rounded-md cursor-pointer border transition-colors ${emailMode === "hyperlocal" ? "border-cyan-400/60 bg-cyan-500/10" : "border-slate-200 bg-white hover:border-slate-400"}`}
                    data-testid="email-mode-hyperlocal"
                  >
                    <input
                      type="radio"
                      name="email-mode"
                      value="hyperlocal"
                      checked={emailMode === "hyperlocal"}
                      onChange={() => setEmailMode("hyperlocal")}
                      className="mt-0.5 accent-cyan-400"
                    />
                    <div>
                      <span className="text-sm text-slate-900 font-medium">Hyperlocal</span>
                      <p className="text-xs text-slate-500 mt-0.5">Local business outreach.</p>
                    </div>
                  </label>
                  <label
                    className={`flex items-start gap-2 p-2.5 rounded-md cursor-pointer border transition-colors ${emailMode === "agency_reach_out" ? "border-cyan-400/60 bg-cyan-500/10" : "border-slate-200 bg-white hover:border-slate-400"}`}
                    data-testid="email-mode-agency"
                  >
                    <input
                      type="radio"
                      name="email-mode"
                      value="agency_reach_out"
                      checked={emailMode === "agency_reach_out"}
                      onChange={() => setEmailMode("agency_reach_out")}
                      className="mt-0.5 accent-cyan-400"
                    />
                    <div>
                      <span className="text-sm text-slate-900 font-medium">Agency Reach Out</span>
                      <p className="text-xs text-slate-500 mt-0.5">B2B pitch + 3-day follow-up.</p>
                    </div>
                  </label>
                </div>

                {/* Sequence-generation notice — AI sequences are always
                    generated for NEW campaigns. For EXISTING campaigns we
                    prompt the user right before push so they can choose to
                    keep or overwrite their current sequences. */}
                <div className="mt-3 pt-3 border-t border-cyan-500/20">
                  <p className="text-xs text-slate-500">
                    {instantlyMode === "new"
                      ? "AI email sequences will be generated automatically for this new campaign."
                      : "You'll be asked before push whether to generate new AI sequences (this would overwrite the campaign's current sequences)."}
                  </p>
                </div>
              </div>

              {/* Include risky toggle */}
              <div className="p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeRisky}
                    onChange={e => setIncludeRisky(e.target.checked)}
                    className="w-4 h-4 rounded border-yellow-500/50 bg-slate-100 accent-yellow-500"
                    data-testid="include-risky-checkbox"
                  />
                  <div>
                    <span className="text-sm text-slate-900 font-medium">Include risky (accept-all) emails</span>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Only verified valid emails are pushed by default. Enable to also include risky ones.
                    </p>
                  </div>
                </label>
              </div>

              {/* Push destination checkboxes */}
              <div className="p-3 rounded-lg bg-slate-100/30 border border-slate-200 space-y-2.5">
                <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-2">Push Destinations</p>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pushToInstantly}
                    onChange={e => setPushToInstantly(e.target.checked)}
                    className="w-4 h-4 rounded border-emerald-600/50 bg-slate-100 accent-emerald-600"
                    data-testid="push-to-instantly-checkbox"
                  />
                  <div>
                    <span className="text-sm text-slate-900 font-medium">Instantly</span>
                    <p className="text-xs text-slate-500 mt-0.5">Push leads to the selected Instantly campaign</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pushToGHL}
                    onChange={e => setPushToGHL(e.target.checked)}
                    className="w-4 h-4 rounded border-fuchsia-500/50 bg-slate-100 accent-fuchsia-500"
                    data-testid="push-to-ghl-checkbox"
                  />
                  <div>
                    <span className="text-sm text-slate-900 font-medium">GoHighLevel</span>
                    <p className="text-xs text-slate-500 mt-0.5">Sync contacts and create opportunities in GHL pipeline</p>
                  </div>
                </label>
              </div>

              {/* Lead count preview */}
              <div className="p-3 rounded-lg bg-slate-100 border border-slate-200">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Valid leads:</span>
                  <span className="text-emerald-400 font-medium">{job.verified_count}</span>
                </div>
                {includeRisky && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-slate-500">+ Risky leads:</span>
                    <span className="text-yellow-400 font-medium">{job.risky_count}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm mt-2 pt-2 border-t border-slate-200">
                  <span className="text-slate-700 font-medium">Total to push:</span>
                  <span className="text-slate-900 font-bold">{includeRisky ? job.verified_count + job.risky_count : job.verified_count}</span>
                </div>
                <p className="text-xs text-slate-500 mt-2">Duplicates and previously pushed leads will be excluded automatically.</p>
              </div>

              {/* Safety note */}
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Warning size={12} className="text-yellow-500" />
                Campaign will remain <strong className="text-slate-700">paused</strong> after push. Start it manually in Instantly.
              </p>
            </div>

            <div className="p-4 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0">
              <Button
                variant="outline"
                onClick={() => setShowInstantlyModal(false)}
                disabled={pushing}
                className="border-slate-200 text-slate-700 hover:bg-slate-100"
                data-testid="instantly-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePushToInstantly}
                disabled={pushing || (!pushToInstantly && !pushToGHL) || (instantlyMode === "existing" && !selectedCampaign) || (instantlyMode === "new" && !newCampaignName.trim()) || (campaignUsage?.used_by_other_jobs && !confirmCrossJob)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-40"
                data-testid="instantly-push-btn"
              >
                {pushing ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Pushing...
                  </span>
                ) : (
                  <>
                    <PaperPlaneTilt size={16} className="mr-2" />
                    Push Leads
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-white border border-slate-200 rounded-lg shadow-2xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Warning size={24} className="text-red-500" />
                <h3 className="text-lg font-bold text-slate-900">Delete Job</h3>
              </div>
              <button onClick={() => setShowDeleteModal(false)} className="text-slate-500 hover:text-slate-900"><X size={20} /></button>
            </div>
            <p className="text-slate-500 mb-6">
              This action cannot be undone. This will permanently delete the job <strong className="text-slate-900">"{job.name}"</strong> and all its leads.
            </p>
            <div className="space-y-2 mb-6">
              <label className="text-sm text-slate-500">Type <span className="text-red-500 font-bold">DELETE</span> to confirm:</label>
              <Input value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} placeholder="Type DELETE" className="bg-white border-slate-200 text-slate-900" data-testid="delete-confirm-input" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="border-slate-200 text-slate-700 hover:bg-slate-100" data-testid="delete-cancel-btn">Cancel</Button>
              <Button onClick={handleDelete} disabled={deleteConfirmText !== "DELETE"} className="bg-red-600 hover:bg-red-700 text-slate-900 disabled:opacity-40" data-testid="delete-confirm-btn">Delete Job</Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-500 hover:text-slate-900" data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>{job.name}</h1>
              <StatusBadge status={job.status} />
            </div>
            <JobMeta category={job.category} location={job.location || job.city || job.state || job.country} />
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Push to Instantly - Admin only */}
          {isAdmin && job.status === "completed" && (
            <Button
              onClick={openInstantlyModal}
              disabled={job.verified_count === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold "
              data-testid="push-instantly-btn"
            >
              <PaperPlaneTilt size={18} className="mr-2" />
              Push to Instantly
            </Button>
          )}

          {/* Push to Voice Drop (GHL workflow) - Admin only */}
          {isAdmin && job.status === "completed" && (
            <Button
              onClick={openVoiceDropModal}
              disabled={(job.phone_count || 0) === 0 && job.cleaned_leads_count === 0}
              className="bg-[#006994] hover:bg-[#005A80] text-white font-bold "
              data-testid="push-voice-drop-btn"
              title={(job.phone_count || 0) === 0 ? "No phone-enriched leads to push" : "Enroll into a GHL workflow (voice drop, SMS, etc)"}
            >
              <PhoneCall size={18} className="mr-2" />
              Push to Voice Drop
            </Button>
          )}

          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-[#006994] hover:bg-[#005A80] text-white font-bold " disabled={exporting || cleanedLeads.length === 0} data-testid="export-dropdown-btn">
                <DownloadSimple size={18} className="mr-2" />
                Export
                <CaretDown size={14} className="ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-slate-200 w-56">
              <DropdownMenuItem onClick={() => handleExport("csv", false, false, true)} disabled={exporting || (cleanedLeads.length - job.invalid_count) === 0} className="text-slate-700 focus:bg-slate-100 focus:text-slate-900 cursor-pointer" data-testid="export-csv-all-btn">
                <FileCsv size={16} className="mr-2 text-[#006994]" />
                CSV ({cleanedLeads.length - job.invalid_count} leads)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("csv", false, true)} disabled={exporting || job.verified_count === 0} className="text-slate-700 focus:bg-slate-100 focus:text-slate-900 cursor-pointer" data-testid="export-valid-btn">
                <CheckCircle size={16} className="mr-2 text-emerald-400" />
                CSV Valid Only ({job.verified_count})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("xlsx", false, false, true)} disabled={exporting || (cleanedLeads.length - job.invalid_count) === 0} className="text-slate-700 focus:bg-slate-100 focus:text-slate-900 cursor-pointer" data-testid="export-xlsx-btn">
                <FileXls size={16} className="mr-2 text-blue-400" />
                XLSX
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-100" />
              <DropdownMenuItem onClick={() => handleExport("ghl", false, false, true)} disabled={exporting || (cleanedLeads.length - job.invalid_count) === 0} className="text-slate-700 focus:bg-slate-100 focus:text-slate-900 cursor-pointer" data-testid="export-ghl-btn">
                <FileText size={16} className="mr-2 text-[#0EA5E9]" />
                GHL Format
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("instantly", false, false, true)} disabled={exporting || (cleanedLeads.length - job.invalid_count) === 0} className="text-slate-700 focus:bg-slate-100 focus:text-slate-900 cursor-pointer" data-testid="export-instantly-btn">
                <FileText size={16} className="mr-2 text-orange-400" />
                Instantly CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="outline" onClick={() => { setShowDeleteModal(true); setDeleteConfirmText(""); }} className="border-red-900/50 text-red-400 hover:bg-red-900/20" data-testid="delete-job-btn">
            <Trash size={18} />
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {job.error_message && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400">
          <p className="text-sm font-medium">Error: {job.error_message}</p>
        </div>
      )}

      {/* Stats — cards adapt to enrichment mode so phone-only jobs don't show 0s
           for email-verification metrics that never ran. */}
      {(() => {
        const emailMode = job.enrich_emails !== false;
        const phoneMode = job.enrich_phones === true;
        return (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatBox label="Raw Leads" value={job.raw_leads_count} color="border-slate-300 bg-slate-100" />
            <StatBox label="Cleaned" value={job.cleaned_leads_count} color="border-[#006994]/50 bg-[#006994]/15" />
            {job.cross_job_duplicates > 0 && <StatBox label="Cross-Job Dupes" value={job.cross_job_duplicates} color="border-amber-500/50 bg-amber-100" />}
            {emailMode && (
              <>
                <StatBox label="With Email" value={cleanedLeads.length} color="border-[#0EA5E9]/50 bg-[#0EA5E9]/15" />
                <StatBox label="Verified" value={job.verified_count} color="border-emerald-500/50 bg-emerald-100" />
                <StatBox label="Risky" value={job.risky_count} color="border-amber-500/50 bg-amber-100" />
                <StatBox label="Invalid" value={job.invalid_count} color="border-red-500/50 bg-red-100" />
              </>
            )}
            {phoneMode && (
              <StatBox label="With Phone" value={job.phone_count || 0} color="border-[#006994]/50 bg-[#006994]/15" />
            )}
          </motion.div>
        );
      })()}

      {/* Instantly Push History - Admin only */}
      {isAdmin && pushHistory.length > 0 && (
        <Card className="glass-card border-emerald-600/30 bg-emerald-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-emerald-700 flex items-center gap-2">
              <PaperPlaneTilt size={18} />
              Instantly Push History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pushHistory.slice(0, 1).map((push) => {
                const campaignStatus = push.campaign_status || "unknown";
                const statusStyles = {
                  active:    { bg: "bg-emerald-500/10", text: "text-emerald-400", label: "Campaign active" },
                  paused:    { bg: "bg-zinc-500/10",    text: "text-slate-500",    label: "Campaign paused" },
                  draft:     { bg: "bg-zinc-500/10",    text: "text-slate-500",    label: "Campaign draft" },
                  completed: { bg: "bg-blue-500/10",    text: "text-blue-400",    label: "Campaign completed" },
                  unknown:   { bg: "bg-zinc-500/10",    text: "text-slate-500",    label: "Status unknown" },
                };
                const cs = statusStyles[campaignStatus] || statusStyles.unknown;
                return (
                <div key={push.id} className="flex items-center justify-between p-3 rounded-lg bg-white border border-slate-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{push.campaign_name}</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        push.status === "success" ? "bg-emerald-500/10 text-emerald-400" :
                        push.status === "processing" ? "bg-blue-500/10 text-blue-400 animate-pulse" :
                        push.status === "failed" ? "bg-red-500/10 text-red-400" :
                        "bg-yellow-500/10 text-yellow-400"
                      }`}>
                        {push.status === "processing" ? "pushing..." : push.status}
                      </span>
                      {push.include_risky && <span className="px-1.5 py-0.5 rounded text-xs bg-yellow-500/10 text-yellow-400">+risky</span>}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {push.status === "processing" ? "Push in progress..." : <>{push.row_count} leads pushed{push.attempted_count && push.attempted_count !== push.row_count ? ` (${push.attempted_count} attempted)` : ""} &bull; {new Date(push.pushed_at).toLocaleString()} &bull; by {push.pushed_by_name}</>}
                      {push.ghl_synced > 0 && <> &bull; <span className="text-[#0EA5E9]">{push.ghl_synced} GHL synced</span></>}
                      {push.status !== "processing" && push.row_count > 0 && (!push.ghl_synced || push.ghl_synced === 0) && (
                        <> &bull; <button onClick={() => handleGhlResync(push.id)} className="text-[#0EA5E9] hover:text-[#0EA5E9]/80 underline" data-testid="retry-ghl-sync-btn">Retry GHL Sync</button></>
                      )}
                      {push.sequences_generated && <> &bull; <span className="text-[#006994]">AI sequences generated</span></>}
                    </p>
                    {push.errors?.length > 0 && (
                      <p className="text-xs text-red-400 mt-1 max-w-xl">
                        {push.errors.some(e => e.includes("limit"))
                          ? "Instantly lead upload limit reached. Upgrade your Instantly plan for more uploads."
                          : push.errors.length > 2
                            ? `${push.errors[0]}... and ${push.errors.length - 1} more errors`
                            : push.errors.join("; ")
                        }
                      </p>
                    )}
                  </div>
                  <span
                    className={`text-xs font-medium flex-shrink-0 ml-4 px-2 py-1 rounded ${cs.bg} ${cs.text}`}
                    data-testid="campaign-status-badge"
                  >
                    {cs.label}
                  </span>
                </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leads Tables */}
      <Tabs defaultValue="cleaned" className="space-y-4">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="cleaned" className="data-[state=active]:bg-[#006994]/10 data-[state=active]:text-[#006994]" data-testid="cleaned-leads-tab">
            Cleaned Leads ({cleanedLeads.length})
          </TabsTrigger>
          <TabsTrigger value="raw" className="data-[state=active]:bg-[#006994]/10 data-[state=active]:text-[#006994]" data-testid="raw-leads-tab">
            Raw Leads ({rawLeads.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cleaned">
          <Card className="glass-card border-slate-200">
            <CardHeader className="pb-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <CardTitle className="text-lg text-slate-900">Cleaned & Verified Leads</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 md:w-64">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                    <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search leads..." className="pl-9 bg-white/50 border-slate-200 text-slate-900 text-sm" data-testid="search-leads-input" />
                  </div>
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 bg-white/50 border border-slate-200 rounded-md text-sm text-slate-700 focus:border-[#006994]" data-testid="status-filter-select">
                    <option value="">All Status</option>
                    <option value="valid">Valid</option>
                    <option value="risky">Risky</option>
                    <option value="invalid">Invalid</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-3 text-xs font-medium text-slate-500 uppercase w-[18%]">Business</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-slate-500 uppercase w-[22%]">Email</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-slate-500 uppercase w-[13%]">Phone</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-slate-500 uppercase w-[15%]">Location</th>
                      <th className="text-left py-3 px-3 text-xs font-medium text-slate-500 uppercase w-[10%]">Status</th>
                      {isAdmin && <th className="text-left py-3 px-3 text-xs font-medium text-slate-500 uppercase w-[22%]">Sync</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.length > 0 ? filteredLeads.slice(0, 100).map((lead) => (
                      <tr key={lead.id} className="border-b border-slate-200 hover:bg-slate-100/30 transition-colors">
                        <td className="py-3 px-3">
                          <div className="truncate">
                            <p className="text-sm text-slate-900 font-medium truncate">{lead.business_name}</p>
                            {lead.website && (
                              <a href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#006994] hover:underline truncate block">{lead.website}</a>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-sm text-slate-700 font-mono truncate">{lead.email}</td>
                        <td className="py-3 px-3 text-sm text-slate-500 truncate">{lead.phone}</td>
                        <td className="py-3 px-3 text-sm text-slate-500 truncate">{[lead.city, lead.state].filter(Boolean).join(", ")}</td>
                        <td className="py-3 px-3"><VerificationBadge status={lead.verification_status} /></td>
                        {isAdmin && (
                          <td className="py-3 px-3">
                            {(() => {
                              const mapping = ghlMappings[lead.email?.toLowerCase()];
                              if (!mapping) return <span className="text-xs text-slate-400">—</span>;
                              return (
                                <div className="flex items-center gap-1.5">
                                  {mapping.synced_to_instantly && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-600/15 text-emerald-700">Instantly</span>}
                                  {mapping.synced_to_ghl && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#0EA5E9]/10 text-[#0EA5E9]">GHL</span>}
                                  {mapping.current_ghl_stage_key && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">{mapping.current_ghl_stage_key.replace("_", " ")}</span>}
                                </div>
                              );
                            })()}
                          </td>
                        )}
                      </tr>
                    )) : (
                      <tr><td colSpan={isAdmin ? 6 : 5} className="py-12 text-center text-slate-500">{filteredLeads.length === 0 && cleanedLeads.length === 0 ? "No cleaned leads yet" : "No leads match your search"}</td></tr>
                    )}
                  </tbody>
                </table>
                {filteredLeads.length > 100 && (
                  <p className="text-center text-sm text-slate-500 py-4">Showing 100 of {filteredLeads.length} leads. Export to see all.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="raw">
          <Card className="glass-card border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg text-slate-900">Raw Leads (Before Cleaning) - All Data from Google Maps</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Business</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Phone</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Email</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">City</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">State</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rawLeads.length > 0 ? rawLeads.slice(0, 100).map((lead) => (
                      <tr key={lead.id} className="border-b border-slate-200 hover:bg-slate-100/30 transition-colors">
                        <td className="py-3 px-4 text-sm text-slate-900 font-medium">{lead.business_name}</td>
                        <td className="py-3 px-4 text-sm text-slate-700">{lead.phone || "-"}</td>
                        <td className="py-3 px-4 text-sm text-slate-500 font-mono">{lead.email || "-"}</td>
                        <td className="py-3 px-4 text-sm text-slate-500">{lead.city || "-"}</td>
                        <td className="py-3 px-4 text-sm text-slate-500">{lead.state || "-"}</td>
                        <td className="py-3 px-4 text-sm text-slate-500 truncate max-w-[250px]">{lead.address || "-"}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={6} className="py-12 text-center text-slate-500">No raw leads found</td></tr>
                    )}
                  </tbody>
                </table>
                {rawLeads.length > 100 && (
                  <p className="text-center text-sm text-slate-500 py-4">Showing 100 of {rawLeads.length} leads. Use "Export All" to download complete list.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default JobDetailsPage;
